
import { motion } from 'framer-motion'
import { Zap, AlertTriangle, CheckCircle, Clock } from 'lucide-react'
import { UserLimits } from '@/types'
import { useTranslation } from 'react-i18next'

interface PromptLimitIndicatorProps {
  userLimits: UserLimits | null
  className?: string
  showDetails?: boolean
}

export default function PromptLimitIndicator({ 
  userLimits, 
  className = '', 
  showDetails = false 
}: PromptLimitIndicatorProps) {
  if (!userLimits) return null

  const { t } = useTranslation()
  const remainingPrompts = userLimits.maxPrompts - userLimits.promptCount
  const usagePercentage = (userLimits.promptCount / userLimits.maxPrompts) * 100
  const resetDate = new Date(userLimits.resetDate)
  const daysUntilReset = Math.ceil((resetDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))

  const getStatusColor = () => {
    if (remainingPrompts === 0) return 'text-red-400'
    if (remainingPrompts <= 5) return 'text-yellow-400'
    return 'text-green-400'
  }

  const getProgressColor = () => {
    if (usagePercentage >= 100) return 'from-red-500 to-red-600'
    if (usagePercentage >= 75) return 'from-yellow-500 to-orange-500'
    return 'from-green-500 to-emerald-500'
  }

  const getIcon = () => {
    if (remainingPrompts === 0) return <AlertTriangle className="w-4 h-4" />
    if (remainingPrompts <= 5) return <Zap className="w-4 h-4" />
    return <CheckCircle className="w-4 h-4" />
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-3 ${className}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-2">
          <div className={getStatusColor()}>
            {getIcon()}
          </div>
          <span className="text-white/80 font-medium text-sm">{t('promptLimit.title')}</span>
        </div>
        <div className="flex items-center space-x-1">
          <span className={`font-bold text-sm ${getStatusColor()}`}>
            {remainingPrompts}
          </span>
          <span className="text-white/40 text-sm">
            / {userLimits.maxPrompts}
          </span>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="w-full bg-white/10 rounded-full h-2 mb-2">
        <motion.div 
          initial={{ width: 0 }}
          animate={{ width: `${usagePercentage}%` }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className={`h-2 rounded-full bg-gradient-to-r ${getProgressColor()}`}
        />
      </div>

      {showDetails && (
        <div className="space-y-2 text-xs text-white/60">
          <div className="flex items-center justify-between">
            <span>{t('promptLimit.used')}</span>
            <span className="text-white/80">{userLimits.promptCount}</span>
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-1">
              <Clock className="w-3 h-3" />
              <span>{t('promptLimit.reset')}</span>
            </div>
            <span className="text-white/80">{t('promptLimit.daysLeft', { days: daysUntilReset })}</span>
          </div>
        </div>
      )}

      {remainingPrompts === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mt-2 p-2 bg-red-500/20 border border-red-500/30 rounded-lg"
        >
          <p className="text-red-300 text-xs text-center">{t('promptLimit.limitReached', { days: daysUntilReset })}</p>
        </motion.div>
      )}

      {remainingPrompts > 0 && remainingPrompts <= 5 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mt-2 p-2 bg-yellow-500/20 border border-yellow-500/30 rounded-lg"
        >
          <p className="text-yellow-300 text-xs text-center">{t('promptLimit.lowLeft', { count: remainingPrompts })}</p>
        </motion.div>
      )}
    </motion.div>
  )
}
